import clsx from 'clsx';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { pagePreviewSrc } from '../../lib/pagePreviewSrc';
import { type CurationSimilarItem } from './useCurationSession';

/**
 * The flagged pages that look like the page under review (DR-3318). Every member is
 * selected by default; a deselected one stays visible (greyed) so the curator can always
 * see the full blast radius of the verdict they are about to cast.
 */
export function SimilarPagesStrip({
  members,
  selectedIds,
  anchorCategory,
  onToggle,
  disabled,
}: {
  members: CurationSimilarItem[];
  selectedIds: Set<string>;
  /** The reviewed page's category — null narrows what a confirm can reach (DR-3318). */
  anchorCategory: string | null;
  onToggle: (pageId: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg dark:border dark:border-zinc-700 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
          Similar flagged pages
        </span>
        <span className="text-xs text-gray-500 dark:text-zinc-400">
          {selectedIds.size} of {members.length} selected
        </span>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-1">
        {members.map((member, index) => (
          <SimilarPageTile
            key={member.pageId}
            member={member}
            position={index + 1}
            total={members.length}
            selected={selectedIds.has(member.pageId)}
            onToggle={onToggle}
            disabled={disabled}
          />
        ))}
      </div>

      <div className="space-y-1 text-xs text-gray-400 dark:text-zinc-500">
        <p>
          Your verdict applies to the page above and every selected page here — a selected page
          labelled differently is <span className="font-medium">set</span> to your verdict's
          category. Click a page to leave it out.
        </p>
        {!anchorCategory && (
          <p>
            The page above is uncategorized, so Correct applies only to the selected pages that are
            uncategorized too.
          </p>
        )}
      </div>
    </div>
  );
}

function SimilarPageTile({
  member,
  position,
  total,
  selected,
  onToggle,
  disabled,
}: {
  member: CurationSimilarItem;
  /** 1-based place in the strip — the tiles are otherwise indistinguishable to a screen reader. */
  position: number;
  total: number;
  selected: boolean;
  onToggle: (pageId: string) => void;
  disabled: boolean;
}) {
  const category = member.currentCategory ?? 'uncategorized';
  const percent = Math.round(member.similarity * 100);
  const describes = `page ${position} of ${total} (${category}, ${percent}% similar)`;

  return (
    <div className="w-28 shrink-0 space-y-1">
      <button
        type="button"
        aria-pressed={selected}
        aria-label={
          selected ? `Exclude ${describes} from this verdict` : `Include ${describes} in this verdict`
        }
        disabled={disabled}
        onClick={() => onToggle(member.pageId)}
        className={clsx(
          'relative block aspect-3/4 w-full overflow-hidden rounded-md border bg-gray-100 shadow-sm transition dark:bg-zinc-700',
          selected
            ? 'border-indigo-500 ring-2 ring-indigo-500/40 dark:border-indigo-400'
            : 'border-gray-200 opacity-40 grayscale hover:opacity-70 dark:border-zinc-600',
          disabled && 'cursor-not-allowed',
        )}
      >
        <img
          src={pagePreviewSrc(member.pageId, { resize: 'x400' })}
          alt=""
          loading="lazy"
          className="h-full w-full object-contain"
        />
        <span className="absolute left-1 top-1 rounded bg-black/60 px-1 text-[10px] font-medium text-white">
          {percent}%
        </span>
        {selected && (
          <CheckCircleIcon className="absolute right-1 top-1 size-5 text-indigo-600 drop-shadow dark:text-indigo-400" />
        )}
      </button>

      <div
        className="truncate text-center text-xs text-gray-600 dark:text-zinc-300"
        title={category}
      >
        {category}
      </div>

      <a
        href={`/document-pages/${member.pageId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="block text-center text-xs text-indigo-600 hover:underline dark:text-indigo-400"
      >
        Open ↗
      </a>
    </div>
  );
}
